<template>
    <section :class="this.getSectionClass" >
        <div class="section__page-container" :style="this.getPageContainerStyle">
            <div class="section__content-container">
                <div class="section__grid">
                    <div v-if="section.introtext" class="section__grid-column section__grid-column--text">
                        <introtext-element :introtext="section.introtext">
                            <template slot="extra-button">
                                <slot name="extra-button" />
                            </template>
                        </introtext-element>
                    </div>
                    <div v-if="section.media" class="section__grid-column section__grid-column--media">
                        <media-element
                            :media="section.media"
                            :image-settings="section.settings.image"
                            :video-settings="section.settings.video" />
                    </div>
                    <div v-if="section.component && !$slots.component" class="section__grid-column section__grid-column--component">
                        <accordion-element
                            v-if="section.type == 'section-accordion'"
                            :panels="section.component.panels"
                            :settings="section.settings.accordion" />

                        <accordion-media-switch-element
                            v-if="section.type == 'section-accordion-media-switch'"
                            :panels="section.component.panels"
                            :settings="section.settings.accordion" />

                        <slideshow-element
                            v-if="section.type == 'section-slideshow'"
                            :slides="section.component.slides"
                            :settings="section.settings.slideshow"/>

                        <blockquote-element
                            v-if="section.type == 'section-blockquote'"
                            :blockquote="section.component.blockquote" />

                        <a-b-slider-element
                            v-if="section.type == 'section-ab-slider'"
                            :abslider="section.component.abslider"
                            :settings="section.settings.abslider" />

                        <image-switch-element
                            v-if="section.type == 'section-image-switch'"
                            :items="section.component.items" />

                        <thumbnails-element
                            v-if="section.type == 'section-thumbnails'"
                            :thumbnails="section.component.thumbnails"
                            :settings="section.settings.thumbnails" />

                        <choice-element
                            v-if="section.type == 'section-single-multiple-choice'"
                            :choices="section.component.choices"
                            :feedback="section.component.feedback"
                            :settings="section.settings.choice" />

                        <value-slider-element
                            v-if="section.type == 'section-value-slider'"
                            :feedback="section.component.feedback"
                            :slider="section.component.valueSlider" />

                        <sortable-options-element
                            v-if="section.type == 'section-sortable-options'"
                            :feedback="section.component.feedback"
                            :options="section.component.sortableOptions"
                            />

                        <splitter-element
                            v-if="section.type == 'section-splitter'"
                            :splitter="section.component.splitter"
                            />

                        <consumption-calculator-element
                            v-if="section.type == 'section-consumption-calculator'"
                            :consumptionCalculator="section.component.consumptionCalculator" />
                    </div>

                    <div v-if="$slots.component" class="section__grid-column section__grid-column--component">
                        <slot name="component" />
                    </div>
                </div>
            </div>
            <slot />
        </div>
    </section>
</template>

<script>
import BlockquoteElement from '@/components/elements/BlockquoteElement.vue';
import ABSliderElement from '@/components/elements/ABSliderElement.vue';
import ImageSwitchElement from '@/components/elements/ImageSwitchElement.vue';
import AccordionElement from '@/components/elements/AccordionElement.vue';
import AccordionMediaSwitchElement from '@/components/elements/AccordionMediaSwitchElement.vue';
import SlideshowElement from '@/components/elements/SlideshowElement.vue';
import ThumbnailsElement from '@/components/elements/ThumbnailsElement.vue';
import SplitterElement from '@/components/elements/SplitterElement.vue';

import ConsumptionCalculatorElement from '@/components/elements/specials/ConsumptionCalculatorElement.vue';

import ChoiceElement from '@/components/elements/quiz/ChoiceElement.vue';
import ValueSliderElement from '@/components/elements/quiz/ValueSliderElement.vue';
import SortableOptionsElement from '@/components/elements/quiz/SortableOptionsElement.vue';

export default {
    name: 'BaseSection',
    components: {
        BlockquoteElement,
        AccordionElement,
        AccordionMediaSwitchElement,
        SlideshowElement,
        ImageSwitchElement,
        ABSliderElement,
        ThumbnailsElement,
        ChoiceElement,
        ValueSliderElement,
        SortableOptionsElement,
        SplitterElement,
        ConsumptionCalculatorElement,
    },
    props: {
        section: {
            type: Object,
            required: true,
        }
    },
    computed: {
        getSectionClass() {

            const foregroundModifier = this.getForegroundModifier();
            const backgroundModifier = this.getBackgroundModifier();
            const textBehaviorModifier = this.getTextBehaviorModifier();

            return {
                'section': true,
                [this.section.type]: true,
                'section--has-text': this.section.introtext,
                'section--has-media': this.section.media,
                'section--has-component': this.section.component,

                [foregroundModifier]: foregroundModifier ? true : false,
                [backgroundModifier]: backgroundModifier ? true : false,
                [textBehaviorModifier]: textBehaviorModifier ? true : false,

                [this.getLayoutModifier()]: true,
                [this.getContentSizeModifier()]: true,
            };
        },
        getPageContainerStyle() {

            if (!this.section.settings.section) {
                return null;
            }

            const { backgroundColor, backgroundImage } = this.section.settings.section;
            const styles = {
                backgroundColor: backgroundColor || false,
            };

            if (backgroundImage) {
                styles['backgroundImage'] = `url(${backgroundImage.url})`;
            }

            return styles;
        }
    },
    methods: {
        getForegroundModifier() {

            if (!this.section.settings.section) {
                return null;
            }

            const { foreground } = this.section.settings.section;
            const foregroundIsDark = foreground == 'dark';

            return foregroundIsDark ? 'section--foreground-dark' : 'section--foreground-light';
        },

        getBackgroundModifier() {

            if (!this.section.settings.section) {
                return null;
            }

            const { backgroundColor, backgroundImage } = this.section.settings.section;
            const hasBackground = backgroundColor || backgroundImage || this.section.type == 'section-text-background';

            return hasBackground ? 'section--has-background' : 'section--no-background';
        },

        getTextBehaviorModifier() {

            if (!this.section.settings.section) {
                return null;
            }

            const { textBehavior } = this.section.settings.section;

            return `section--text-behavior-${textBehavior}`;
        },

        getLayoutModifier() {

            let layouModifier = 'stacked';
            const { settings } = this.section;

            if (settings.layout && settings.layout.columns) {
                layouModifier = settings.layout.columns;
            }

            return `section--layout-${layouModifier}`;
        },

        getContentSizeModifier() {

            let sizeModifier = 'content-fit';
            const { settings } = this.section;

            if (settings.layout && settings.layout.size) {
                sizeModifier = settings.layout.size;
            }

            return `section--size-${sizeModifier}`;
        }
    }
}
</script>
